import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const chapters = [
  {
    num: "01",
    title: "Our Mission",
    body: "The Hive is a central gathering place and resource hub for Madison County — a warm, judgment-free space where mothers, families, and individuals find hope, guidance, and tangible aid. Like a hive, we believe a community is strongest when everyone has a place in it.",
    testid: "about-chapter-mission",
  },
  {
    num: "02",
    title: "Who We Serve",
    body: "Expecting and new mothers, growing families, neighbors walking the road of recovery, and anyone in Madison County & the surrounding areas who needs a hand. If you're facing a hard season, our door — and our hearts — are open.",
    testid: "about-chapter-who",
  },
  {
    num: "03",
    title: "Why It Matters",
    body: "In rural communities, help can feel far away. We bring it close: supplies, mentorship, and trusted referrals to local health, housing, and financial programs. Because no one should have to navigate life's hardest moments alone.",
    testid: "about-chapter-why",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden bg-cream py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <Reveal>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-gold-deep" data-testid="about-eyebrow">
              Chapter 01 — Who We Are
            </p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="about-heading">
              A hive of help, <span className="italic text-gold-deep">rooted in home.</span>
            </h2>
          </Reveal>

          <div className="mt-14 space-y-14">
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div className="group relative border-l-2 border-gold/30 pl-8 transition-colors duration-500 hover:border-gold" data-testid={c.testid}>
                  <span className="text-outline-gold pointer-events-none absolute -left-5 -top-7 font-display text-7xl font-bold opacity-60 sm:text-8xl">
                    {c.num}
                  </span>
                  <h3 className="relative font-display text-xl font-semibold text-ink sm:text-2xl">{c.title}</h3>
                  <p className="relative mt-3 max-w-xl text-base leading-relaxed text-ink-soft">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div ref={imgRef} className="relative flex items-center">
          <motion.div style={{ y: yImg }} className="w-full">
            <Reveal delay={0.15}>
              <div className="soft-shadow relative overflow-hidden rounded-[2rem] border border-gold/30 bg-white p-3">
                <img
                  src="/assets/hive-words-banner.webp"
                  alt="Encourage, Celebrate Life, Thrive, Serve, Overcome, New Life, Grow, Give, Love — Joyful in Hope"
                  className="w-full rounded-[1.5rem] object-cover"
                  data-testid="about-words-image"
                />
                <div className="pointer-events-none absolute inset-3 rounded-[1.5rem] ring-1 ring-inset ring-gold/20" />
              </div>
              <p className="mt-6 text-center font-script text-3xl text-gold-deep" data-testid="about-script-quote">
                — Joyful in Hope —
              </p>
            </Reveal>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
